import { LoadingSpinner } from '@shared/components/LoadingSpinner/LoadingSpinner';
import React, { useContext } from 'react';
import { useParams } from 'react-router-dom';
import NotFoundPage from '@/shared/components/NotFoundPage';
import { UserContext } from '@/shared/contexts/UserContext';
import { useMessageService } from '@/shared/services/MessageService/MessageServiceContext';
import { ProfileHeader } from '../account-settings/components/profile/ProfileHeader';
import { ActionMenus } from './components/ActionMenus';
import { ConsultationRequestsTable } from './components/CallRequestsTable';
import { ReviewsTable } from './components/ReviewsTable';
import { useMenuActions } from './hooks/useMenuActions';
import { useUserProfileData } from './hooks/useUserProfileData';

const UserProfilePublicPage: React.FC = () => {
  const { userName } = useParams<{ userName: string }>();
  const { user } = useContext(UserContext);
  const { openMessage, closeMessage } = useMessageService();

  const {
    profileUser,
    userNotFound,
    consultationRequests,
    outgoingRequests,
    myRequestsToThisUser,
    reviews,
    loading,
    reactionStats,
    refetchUserData,
  } = useUserProfileData(userName);

  const {
    reviewMenuAnchor,
    handleReviewMenuOpen,
    handleReviewMenuClose,
    handleReviewAction,
    callRequestMenuAnchor,
    handleCallRequestMenuOpen,
    handleCallRequestMenuClose,
    handleCallRequestAction,
    selectedCallRequestStatus,
  } = useMenuActions({
    consultationRequests,
    myRequestsToThisUser,
    openMessage,
    closeMessage,
    refetchUserData,
  });

  const isOwnProfile = user?.id !== undefined && user.id === profileUser?.id;
  const showDealList = profileUser?.showDealList ?? true;

  if (loading) {
    return <LoadingSpinner variant="circular" size="large" message="Loading profile..." fullScreen />;
  }

  if (userNotFound) {
    return <NotFoundPage />;
  }

  if (!profileUser) {
    return null;
  }

  return (
    <div className="user-profile-page">
      <ProfileHeader user={profileUser} isEditing={false} reactionStats={reactionStats} totalReviews={reviews.length} />

      <div className="user-profile-page__sections">
        {isOwnProfile && consultationRequests.length > 0 && (
          <ConsultationRequestsTable
            consultationRequests={consultationRequests}
            onMenuOpen={handleCallRequestMenuOpen}
            title="Incoming Requests"
            subtitle="Consultation requests others have sent to you"
          />
        )}

        {isOwnProfile && outgoingRequests.length > 0 && (
          <ConsultationRequestsTable
            consultationRequests={outgoingRequests}
            onMenuOpen={handleCallRequestMenuOpen}
            title="Outgoing Requests"
            subtitle="Consultation requests you have sent to others"
          />
        )}

        {!isOwnProfile && myRequestsToThisUser.length > 0 && (
          <ConsultationRequestsTable
            consultationRequests={myRequestsToThisUser}
            onMenuOpen={handleCallRequestMenuOpen}
            subtitle={`Your consultation requests with ${profileUser.username || 'this user'}`}
          />
        )}

        {showDealList ? (
          <ReviewsTable
            reviews={reviews}
            profileUser={profileUser}
            loading={loading}
            isOwnProfile={isOwnProfile}
            onMenuOpen={(e, id) =>
              handleReviewMenuOpen(
                e,
                id,
                reviews.filter((r) => r.companyId !== undefined),
              )
            }
          />
        ) : (
          <div className="user-profile-page__deal-list-private">This user's deal list is not public.</div>
        )}
      </div>

      <ActionMenus
        reviewMenuAnchor={reviewMenuAnchor}
        onReviewMenuClose={handleReviewMenuClose}
        onReviewAction={handleReviewAction}
        callRequestMenuAnchor={callRequestMenuAnchor}
        onCallRequestMenuClose={handleCallRequestMenuClose}
        onCallRequestAction={handleCallRequestAction}
        isOwnProfile={isOwnProfile}
        selectedRequestStatus={selectedCallRequestStatus}
      />
    </div>
  );
};

export default UserProfilePublicPage;
